import React, { useEffect, useState } from "react";
import { ProfilePicture } from "../profile/ProfilePicture.tsx";
import {
  useTeacherFeedbackMutation,
  useTeacherFeedbackQuery,
} from "../../api/teacherApi.ts";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router";
import { useAuth } from "react-oidc-context";
import ActionButton from "../ActionButton.tsx";
import TextInputField from "../TextInputField.tsx";

interface FeedbackFormProps {
  teacherId: string;
  firstName?: string;
  lastName?: string;
  profilePicture?: string;
}

const FeedbackForm = ({
  teacherId,
  firstName,
  lastName,
  profilePicture,
}: FeedbackFormProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState("");

  const { data: existingFeedback } = useTeacherFeedbackQuery(teacherId);
  const { mutate: putTeacherFeedback, isPending } =
    useTeacherFeedbackMutation(teacherId);

  useEffect(() => {
    const text = existingFeedback?.text;
    if (text) {
      setFeedback(text);
    }
  }, [existingFeedback]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (feedback.trim()) {
      putTeacherFeedback(
        { text: feedback },
        {
          onSuccess: () => {
            toast.success("Successfully posted feedback!");
            navigate(`/user/${user?.profile.sub}/bookings`);
          },
          onError: () => {
            toast.error("Could not post feedback. Please try again later.");
          },
        },
      );
    }
  };

  return (
    <div className="mx-auto max-w-md rounded-lg bg-white p-6 shadow-md">
      <h2 className="mb-2 text-2xl font-bold">Give Feedback</h2>
      <h4 className="mb-6 text-sm">
        Your feedback will be displayed in the testimonials section of the
        teachers profile.
      </h4>

      <div className="mb-6 flex items-center gap-3">
        <div className="relative h-28 w-28">
          <ProfilePicture
            firstName={firstName}
            lastName={lastName}
            profilePictureUrl={profilePicture}
            iconSize={72}
          />
        </div>
        <span className="text-xl">
          {firstName} {lastName}
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        <TextInputField
          id={"feedbackTextInput"}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          className="focus:border-soluud-primary focus:ring-soluud-primary/30 w-full resize-none rounded-lg border border-gray-300 p-3 transition-colors duration-150 focus:ring-2 focus:outline-none"
          disabled={isPending}
        />
        <ActionButton
          className={"mt-4"}
          disabled={!feedback.trim() || isPending}
          isLoading={isPending}
          type={"submit"}
        >
          {isPending ? "Submitting..." : "Submit Feedback"}
        </ActionButton>
      </form>
    </div>
  );
};

export default FeedbackForm;
